'use client'

import {
    Children,
    type CSSProperties,
    type DetailedHTMLProps,
    type HTMLAttributes,
    type ReactNode,
    type TouchEvent,
    useCallback,
    useContext,
    useEffect,
    useId,
    useMemo,
    useState,
} from 'react'

import cn from 'clsx'
import { ThemeContext } from '@/utils/themeContext'

import { CarouselControls } from './CarouselControls'

import css from './Carousel.module.css'

export interface CarouselProps
    extends Omit<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'onChange'> {
    /** the contents of the carousel, can be any HTML */
    children: ReactNode
    /** whether or not the carousel should progress on a timeout */
    autoProgresses?: boolean
    /** when at the end the carousel repeats back to the next item  */
    repeat?: boolean
    /** length in milliseconds the automatic timeout takes */
    timeout?: number
    /** whether or not the carousel should display iconButtons that the user can use to navigate between the slides */
    sideButtons?: boolean
    /** handle active index change */
    onChange?: (activeIndex: number) => void
    /** id of carousel to pass in, in case there are more than 1 carousel in the same page */
    id?: string
    /** used to style the carousel controls when used within the TestimonialCarousel */
    variant?: 'image' | 'testimonial'
    /** title label for the back icon button */
    previousButtonLabel: string
    /** title label for the forward icon button */
    nextButtonLabel: string
    /** Name of each element of the carousel used for the dots indicators. */
    dotIndicatorLabels: string[]
    /** Show gallery borders. */
    showBorders?: boolean
}

interface CarouselCustomProperties extends CSSProperties {
    '--active-index': number
}

function Carousel({
    id,
    children,
    timeout = 8000,
    sideButtons = true,
    autoProgresses = false,
    className,
    variant = 'image',
    onChange,
    previousButtonLabel = 'Previous',
    nextButtonLabel = 'Next',
    showBorders,
    style,
    repeat,
    dotIndicatorLabels,
    ...rest
}: CarouselProps) {
    const uniqueId = useId()
    const length = Children.count(children)
    const childrenArray = Children.toArray(children)
    const finalChildren = useMemo(() => {
        const elements =
            repeat && length >= 3
                ? [...childrenArray.slice(-2), ...childrenArray, ...childrenArray.slice(0, 2)]
                : childrenArray
        return elements.map((el) => ({ id: Math.random().toString(16).slice(2).toString(), el }))
    }, [childrenArray, length, repeat])

    const { theme = 'todoist' } = useContext(ThemeContext)

    const [activeIndex, setActiveIndex] = useState(0)
    const [pauseSlide, setPauseSlide] = useState(false)
    const [shouldTransition, setShouldTransition] = useState(true)
    const [touchStart, setTouchStart] = useState(0)
    const [transitioning, setTransitioning] = useState(false)

    const onChangeHandler = useCallback(
        (newIndex: number) => {
            if (repeat) {
                // We need to avoid getting ahead of the transition which moves the
                // gallery back to the start to give the loop effect
                if (transitioning) {
                    return
                }

                setTransitioning(true)
                setActiveIndex(newIndex)
            } else {
                setActiveIndex((newIndex + length) % length)
            }
        },
        [length, repeat, transitioning],
    )

    function onTransitionEndHandler() {
        if (!repeat) {
            return
        }

        setTransitioning(false)

        if (activeIndex >= length) {
            setShouldTransition(false)
            setActiveIndex(0)
        }

        if (activeIndex <= -1) {
            setShouldTransition(false)
            setActiveIndex(length - 1)
        }
    }

    function onTouchStartHandler(e: TouchEvent) {
        setTouchStart(e.touches[0].clientX)
    }

    function onTouchMoveHandler(e: TouchEvent) {
        if (!touchStart) {
            return
        }

        const touchEnd = e.touches[0].clientX
        const diff = touchStart - touchEnd
        if (Math.abs(diff) > 5) {
            onChangeHandler(Math.sign(diff) >= 0 ? 1 : -1)
        }

        setTouchStart(0)
    }

    useEffect(() => {
        setShouldTransition(true)
        if (onChange) {
            onChange(activeIndex)
        }
    }, [activeIndex, onChange])

    useEffect(() => {
        let interval: number

        if (autoProgresses) {
            interval = window.setInterval(() => !pauseSlide && onChangeHandler(1), timeout + 10)
            return () => clearInterval(interval)
        }

        return function autoProgressesCleanup() {
            if (interval) {
                clearInterval(interval)
            }
        }
    }, [timeout, autoProgresses, pauseSlide, onChangeHandler])

    const cssCustomProperties: CarouselCustomProperties = {
        '--active-index': activeIndex + (repeat ? 2 : 0),
    }

    return (
        <div
            onMouseEnter={() => setPauseSlide(true)}
            onMouseLeave={() => setPauseSlide(false)}
            className={cn(css.carousel, className)}
            style={{ ...style, ...cssCustomProperties }}
            {...rest}
        >
            <div
                id={id || uniqueId}
                className={cn(css.scrollContainer, css[theme], { [css.borders]: showBorders })}
                onTouchStart={onTouchStartHandler}
                onTouchMove={onTouchMoveHandler}
            >
                <ul
                    className={cn(css.gallery, { [css.transitionEnabled]: shouldTransition })}
                    onTransitionEnd={onTransitionEndHandler}
                >
                    {finalChildren.map(({ id, el }) => (
                        <li key={`item-${id}`} className={css.carouselItem}>
                            {el}
                        </li>
                    ))}
                </ul>
            </div>
            {dotIndicatorLabels.length > 1 && (
                <CarouselControls
                    variant={variant}
                    dotIndicatorLabels={dotIndicatorLabels}
                    sideButtons={sideButtons}
                    onChange={(value) => {
                        if (typeof value === 'number') {
                            onChangeHandler(value)
                        } else if (value === 'previous') {
                            onChangeHandler(activeIndex > 0 ? activeIndex - 1 : length - 1)
                        } else {
                            onChangeHandler(activeIndex < length ? activeIndex + 1 : 0)
                        }
                    }}
                    activeIndex={(activeIndex + length) % length}
                    previousButtonLabel={previousButtonLabel}
                    nextButtonLabel={nextButtonLabel}
                />
            )}
        </div>
    )
}

function CarouselContent({ image, caption }: { image: ReactNode; caption: ReactNode }) {
    return (
        <div className={css.content}>
            <div className={css.image}>{image}</div>
            <div className={css.caption}>{caption}</div>
        </div>
    )
}

export { Carousel, CarouselContent }
